import { useTranslation } from 'react-i18next';
import { useDOI } from '@/contexts/DOIContext';
import { Button } from '@/components/ui/button';
import { VStack, HStack } from '@/components/core';
import { Mail, Clock, Lock, Send, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Modal component for DOI (Double Opt-In) email verification.
 * Displays countdown timer, locked state, and resend functionality.
 * Uses design system tokens and follows LowBalanceModal pattern.
 */
export function DOIVerificationModal() {
  const { t } = useTranslation('emailVerification');
  const {
    isModalOpen,
    isLocked,
    timeRemaining,
    formattedTime,
    resendEmail,
    resendCooldown,
    isResending,
    resendSuccess,
  } = useDOI();

  if (!isModalOpen) return null;

  // Determine display mode
  const showCountdown = !isLocked && timeRemaining > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop - not dismissible */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-token-md bg-card rounded-2xl shadow-token-modal overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header with gradient */}
        <div
          className={cn(
            'bg-gradient-to-br p-token-xl text-center',
            isLocked
              ? 'from-error via-error to-error'
              : 'from-warning via-warning to-warning'
          )}
        >
          <HStack align="center" justify="center" className="mb-token-md">
            <HStack
              align="center"
              justify="center"
              className="w-16 h-16 rounded-full bg-foreground/20 backdrop-blur-sm"
            >
              {isLocked ? (
                <Lock className="w-8 h-8 text-foreground" />
              ) : (
                <Mail className="w-8 h-8 text-foreground" />
              )}
            </HStack>
          </HStack>
          <h2 className="text-2xl font-bold text-foreground mb-token-sm">
            {isLocked
              ? t('doi.modal.locked.title')
              : t('doi.modal.title')}
          </h2>
          <p className="text-foreground/80">
            {isLocked
              ? t('doi.modal.locked.subtitle')
              : t('doi.modal.subtitle')}
          </p>
        </div>

        {/* Content */}
        <VStack gap="md" className="p-token-lg bg-card">
          {/* Countdown Display */}
          {showCountdown && (
            <VStack gap="sm" align="center" className="p-token-md rounded-token-card bg-warning/10 border border-warning/30">
              <HStack align="center" gap="sm">
                <Clock className="h-5 w-5 text-warning" />
                <span className="text-sm text-warning">
                  {t('doi.modal.countdownPrefix')}
                </span>
              </HStack>
              <span className="text-3xl font-bold text-warning font-mono">
                {formattedTime}
              </span>
              <span className="text-sm text-warning">
                {t('doi.modal.countdownSuffix')}
              </span>
            </VStack>
          )}

          {/* Locked State Display */}
          {isLocked && (
            <VStack gap="sm" align="center" className="p-token-md rounded-token-card bg-error/10 border border-error/30">
              <Lock className="h-8 w-8 text-error" />
              <span className="text-sm text-error text-center">
                {t('doi.modal.locked.hint')}
              </span>
            </VStack>
          )}

          {/* Action Buttons */}
          <VStack gap="sm" className="pt-token-sm">
            {/* Resend Button */}
            <Button
              onClick={resendEmail}
              disabled={resendCooldown > 0 || isResending}
              className={cn(
                'w-full',
                resendSuccess
                  ? 'bg-success hover:bg-success/90'
                  : 'bg-gradient-to-r from-brand-primary to-brand-secondary hover:opacity-90'
              )}
            >
              {resendSuccess ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-token-sm" />
                  {t('doi.modal.resendSuccess')}
                </>
              ) : isResending ? (
                <>
                  <Send className="h-4 w-4 mr-token-sm animate-pulse" />
                  {t('doi.modal.resendButton')}
                </>
              ) : resendCooldown > 0 ? (
                t('doi.modal.resendCooldown', { seconds: resendCooldown })
              ) : (
                <>
                  <Send className="h-4 w-4 mr-token-sm" />
                  {t('doi.modal.resendButton')}
                </>
              )}
            </Button>

            {/* Spam hint */}
            <p className="text-sm text-muted-foreground text-center">
              {t('doi.modal.checkSpam')}
            </p>
          </VStack>
        </VStack>
      </div>
    </div>
  );
}

export default DOIVerificationModal;
